import React, {
  ForwardedRef,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState
} from "react";
import {
  StewartDialog,
  StewartDialogActions,
} from "controls/global/dialogs/stewart-dialog/StewartDialog";
import {
  Box,
  IconButton,
  Typography,
  styled,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  borderRadius,
  borderSize,
  colors,
  fontFamily,
  fontSize,
  fontWeight,
  gradients,
  iconSize,
  margin,
  padding,
  scrollBar,
} from "theme/defaultStyle";
import ActionButton from "controls/global/action-button";
import {
  DialogRefType,
  // FileUploadStatus
} from "entities/UIModel";
import TextInputField from "controls/global/text-input-field";
import EditFilledWhiteIcon from "theme/icons/EditFilledWhiteIcon";
import useFormWrapper from "utils/custom-hooks/useFormWrapper";
import { showToast } from "controls/global/stewart-toast/showToast";

const StyledDialog = styled(StewartDialog)({
  "& > .MuiDialog-container > .MuiDialog-paper": {
    maxHeight: "calc(95vh)",
    maxWidth: "830px", // "calc(80vw)",
    minWidth: "830px", // "calc(40vw)",
    display: "flex",
    rowGap: "24px",
    justifyContent: "flex-start",
    padding: "36px 40px 40px 40px",
    overflowX: "auto",
  },
});

const StyledDialogTitle = styled(Box)({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  columnGap: "24px",
  "& .MuiTypography-root": {
    fontFamily: fontFamily.inter,
    fontStyle: "normal",
    fontSize: fontSize.xlarge1,
    fontWeight: fontWeight.bold1,
    lineHeight: "normal",
  }

})

const scrollBarstyle = {
  maxHeight: "250px",
  overflowX: "auto",
  "&::-webkit-scrollbar": {
    width: "14px",
    height: scrollBar.height,
  },
  "&::-webkit-scrollbar-thumb": {
    borderRadius: scrollBar.borderRadius1,
    backgroundColor: colors.grey20,
    border: `${borderSize.medium} solid transparent`,
    backgroundClip: "content-box",
  },
  "&::-webkit-scrollbar-track": {
    background: colors.white,
    boxShadow: "1px 0px 0px 0px #DEDEDE inset",
    borderRadius: "0px 4px 4px 0px",
    border: "1px solid var(--Input-Default-Stoke, #BDBDBD)",
  },
  "&::-webkit-scrollbar-button": {
    display: "none",
  },
};

const IconContainer = styled("div")({
  background: gradients.blueGradient10,
  borderRadius: borderRadius.large,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  width: "56px",
  height: "56px",
  padding: padding.large1,
  "& .MuiSvgIcon-root": {
    color: colors.white,
    fontSize: iconSize.medium2,
  },
});

const StyledDialogContent = styled(Box)({
  // marginBottom: margin.xlarge1,
  fontSize: fontSize.medium,
  fontFamily: fontFamily.inter,
  fontWeight: fontWeight.normal1,
  "& .failed-upload-status": {
    marginTop: "16px",
    marginBottom: "16px",
    fontWeight: fontWeight.bold1,
    fontSize: fontSize.large,
  },
  "& .success-upload-status": {
    marginTop: margin.large,
    marginBottom: margin.large,
    fontWeight: fontWeight.bold1,
    fontSize: fontSize.large,
  },
  "& .failed-status-grid": {
    ...scrollBarstyle
  },
  "& .failed-status-only-grid": {
    ...scrollBarstyle
  },
  "& .failed-status-grid table, .failed-status-only-grid table": {
    "& td": {
      padding: `${padding.small} ${padding.small1}`,
      fontFamily: fontFamily.inter,
      fontSize: fontSize.medium, // fontSize.large,
    },
    "& td div": {
      fontFamily: fontFamily.inter,
      fontSize: fontSize.medium, // fontSize.large,
      fontWeight: fontWeight.normal1,
      lineHeight: "normal", // "150%",
      overflow: "hidden",
      paddingTop: padding.zero,
      textOverflow: "ellipsis",
    },    
  },
  "& .success-status-grid": {
    ...scrollBarstyle
  },
  "& .success-status-only-grid": {
    ...scrollBarstyle
  },
  "& .success-status-grid table, .success-status-only-grid table": {
    "& td": {
      paddingTop: "0px",
      paddingBottom: "0px",
    },  
    "& td div": {
      fontFamily: fontFamily.inter,
      fontSize: fontSize.medium, // fontSize.large,
      fontWeight: fontWeight.normal1,
      lineHeight: "normal", // "150%",
      overflow: "hidden",
      paddingTop: padding.zero,
      textOverflow: "ellipsis",
    },
  },

});

const StyledDialogAction = styled(StewartDialogActions)({
  justifyContent: "right",
  paddingBottom: padding.zero,
  paddingTop: padding.zero,
  paddingRight: padding.zero,
  paddingLeft: padding.zero,
});

const CloseIconContainer = styled("div")({
  background: colors.grey10,
  borderRadius: borderRadius.xlarge1,
  display: "flex",
  justifyContent: "center",
  alignItems: "flex-start",
  marginLeft: "auto",
  marginBottom: "auto",
  width: "32px",
  height: "32px",
  padding: "4px",
  "& .iconButton": {
    backgroundColor: colors.grey26,
    display: "flex",
    padding: padding.xsmall1,
    alignItems: "flex-start",    
  },
});

const EMAIL_CHANGED_SUCCESS = "Email for Notifications successfully changed!";

type Props = {
  schema: string;
  currentFieldName: string;
  newFieldName: string;
}

const EditEmailNotificationDialog = ( {schema, currentFieldName, newFieldName}: Props, ref: ForwardedRef<DialogRefType>) => {
  const [open, setOpen] = useState<boolean>(false);
  const { setValue, getValues, clearErrors, trigger } = useFormWrapper();
  const [ saveDisabled, setSaveDisabled ] = useState<boolean>(true);

  const currentEmailAddress = getValues(currentFieldName);

  useImperativeHandle(ref, () => ({
    open: () => {
      setOpen(true);
      setSaveDisabled(true);
      clearErrors(newFieldName);
    },
    close: () => {
      setOpen(false);
      setSaveDisabled(true);
    }
  }));
 

  const validateNewEmailAddress = async () => {
    const newEmailAddress = getValues(newFieldName);
    if (newEmailAddress) {
      clearErrors(newFieldName);
      setValue(`${schema}.isEmailEditedValidationRequired`, true);
      return await trigger(newFieldName);      
    }    
    return true;
  }  

  function handleOnClose() {
    setOpen(false);
  }

  const handleOnChange = (event: React.ChangeEvent<any>) => {
    const newEmailAddress = getValues(newFieldName);    
    if (currentEmailAddress !== newEmailAddress) {
      setSaveDisabled(false);
    }
    else {
      setSaveDisabled(true);
    }
  }
  
  async function handleOnSave() {
    const isValid = await validateNewEmailAddress();

    if (isValid) {
      const newEmailAddress = getValues(newFieldName);
      setValue(currentFieldName, newEmailAddress);
      showToast({
        type: 'success',
        title: EMAIL_CHANGED_SUCCESS,
      });   
      setOpen(false);

    }
  }


  useEffect(()=> {
    if (open) {
      setValue(newFieldName, currentEmailAddress);
    }
  },[open])

  return (
    <>
      <StyledDialog open={open}>
        <StyledDialogTitle>
          <IconContainer>
            <EditFilledWhiteIcon></EditFilledWhiteIcon>
          </IconContainer>
          <Typography>Edit Email for Notifications</Typography>
          <CloseIconContainer>
            <IconButton className="iconButton" onClick={handleOnClose}>
              <CloseIcon />
            </IconButton>
          </CloseIconContainer>
        </StyledDialogTitle>
        <StyledDialogContent>
          <>
            <TextInputField 
              multiline 
              name={newFieldName}
              placeholder="Email For Notifications"
              label="Email For Notifications"
              onChange={handleOnChange}
            />
          </>
        </StyledDialogContent>

        <StyledDialogAction className="DialogAction">
          <ActionButton
            variant="outlined"
            color="primary"
            onClick={handleOnClose}
            // disabled={disable}
          >
            Cancel
          </ActionButton>
          <ActionButton
            variant="outlined"
            color="primary"
            onClick={handleOnSave}
            disabled={saveDisabled}
          >
            Save
          </ActionButton>
        </StyledDialogAction>
      </StyledDialog>
    </>
  );
}

export default forwardRef<DialogRefType, Props>(EditEmailNotificationDialog);